import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { textToSpeechService } from '../services/tts/textToSpeechService';

interface VoiceContextType {
  selectedVoice: SpeechSynthesisVoice | null;
  setSelectedVoice: (voice: SpeechSynthesisVoice | null) => void;
  isVoiceReady: boolean;
}

const VoiceContext = createContext<VoiceContextType | undefined>(undefined);

export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within a VoiceProvider');
  }
  return context;
};

interface VoiceProviderProps {
  children: ReactNode;
}

export const VoiceProvider: React.FC<VoiceProviderProps> = ({ children }) => {
  const [selectedVoice, setSelectedVoiceState] = useState<SpeechSynthesisVoice | null>(null);
  const [isVoiceReady, setIsVoiceReady] = useState(false);

  useEffect(() => {
    // Initialize with best voice
    const initializeVoice = () => {
      const bestVoice = textToSpeechService.getBestEnglishVoice();
      if (bestVoice) {
        setSelectedVoiceState(bestVoice);
        textToSpeechService.setVoice(bestVoice);
        setIsVoiceReady(true);
        console.log('✅ VoiceContext: Voice initialized:', bestVoice.name);
      } else {
        console.log('⚠️ VoiceContext: No voice available yet');
      }
    };

    // Check if we're in production
    const isProduction = window.location.hostname !== 'localhost' &&
      window.location.hostname !== '127.0.0.1' &&
      !window.location.hostname.includes('localhost');

    if (isProduction) {
      console.log('🌐 VoiceContext: Production environment - voices will load on user interaction');
      // In production, don't try to load voices immediately
      // They will be loaded when user interacts with voice selector
    } else {
      console.log('💻 VoiceContext: Development environment - loading voices immediately');
      // Try to initialize immediately in development
      initializeVoice();
    }

    // Also listen for voices changed event
    const handleVoicesChanged = () => {
      console.log('🔄 VoiceContext: Voices changed event');
      initializeVoice();
    };

    if (window.speechSynthesis) {
      // Fix for iPad/Safari where addEventListener might not exist on speechSynthesis
      if (typeof window.speechSynthesis.addEventListener === 'function') {
        window.speechSynthesis.addEventListener('voiceschanged', handleVoicesChanged);
      } else {
        // Fallback for older browsers or stubborn iOS versions
        window.speechSynthesis.onvoiceschanged = handleVoicesChanged;
      }
    }

    return () => {
      if (window.speechSynthesis) {
        if (typeof window.speechSynthesis.removeEventListener === 'function') {
          window.speechSynthesis.removeEventListener('voiceschanged', handleVoicesChanged);
        } else {
          window.speechSynthesis.onvoiceschanged = null;
        }
      }
    };
  }, []);

  const setSelectedVoice = (voice: SpeechSynthesisVoice | null) => {
    setSelectedVoiceState(voice);
    if (voice) {
      textToSpeechService.setVoice(voice);
    }
  };

  return (
    <VoiceContext.Provider value={{ selectedVoice, setSelectedVoice, isVoiceReady }}>
      {children}
    </VoiceContext.Provider>
  );
};

